"use client";

import React, { useEffect, useState } from "react";
import { Controller, useFormContext, FieldPath } from "react-hook-form";
import { UserRound } from "lucide-react";
import { VolunteerFormType } from "../schemas/volunteers.schema";

type VolunteerFormProfileImageProps = {
  fieldPrefix?: string;
};

function VolunteerFormProfileImage({ fieldPrefix }: VolunteerFormProfileImageProps) {
  const {
    control,
    formState: { errors },
    watch,
    setError,
  } = useFormContext<VolunteerFormType | import("../schemas/volunteers.schema").CreateVolunteersFormType>();

  const fieldName = fieldPrefix ? `${fieldPrefix}.profilePicture` : "profilePicture";
  const image = watch(fieldName as FieldPath<VolunteerFormType>);
  const profilePictureErrorMessage = fieldPrefix
    ? (errors as any)?.volunteers?.[Number(fieldPrefix.split(".")[1])]?.profilePicture?.message?.toString()
    : (errors as any)?.profilePicture?.message?.toString();

  const [fileObjectUrl, setFileObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!(image instanceof File)) {
      setFileObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(image);
    setFileObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const imagePreview =
    image == null || image === ""
      ? null
      : typeof image === "string"
        ? image
        : fileObjectUrl;

  return (
    <div className="flex flex-col gap-3">
      <label
        htmlFor={fieldPrefix ? `user-image-${fieldPrefix.replace(/\./g, "-")}` : "user-image-id"}
        className="flex gap-1 cursor-pointer items-center"
      >
        <div
          className="
            w-[100px] aspect-square rounded-full  
            bg-gray-200  
            flex items-center justify-center relative
            cursor-pointer overflow-hidden mx-auto
          "
        >
          {imagePreview ? (
            // eslint-disable-next-line @next/next/no-img-element -- blob: previews and arbitrary storage URLs are not reliable with next/image
            <img
              src={imagePreview}
              alt="preview"
              className="w-full h-full object-cover"
            />
          ) : (
            <UserRound size={50} className="text-gray-500" />
          )}
          <Controller
            name={fieldName as any}
            control={control}
            render={({ field }) => (
              <input
                type="file"
                ref={field.ref}
                name={field.name}
                onBlur={field.onBlur}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (!file.type.includes("image")) {
                      setError(fieldName as any, { message: "File should be an image" });
                      return null;
                    }
                    setError(fieldName as any, { message: "" });
                    field.onChange(file);
                  }
                }}
                className="hidden"
                id={fieldPrefix ? `user-image-${fieldPrefix.replace(/\./g, "-")}` : "user-image-id"}
              />
            )}
          />
        </div>
      </label>
      <p className="text-destructive text-sm text-center">
        {profilePictureErrorMessage}
      </p>
    </div>
  );
}

export default VolunteerFormProfileImage;
